import { differenceInCalendarDays, differenceInYears, format, isSameMonth, isThisYear, parseISO, subMonths, } from 'date-fns';
export const MIN_AGE = 18;
export const MAX_AGE = 120;
export const MIN_YEARS_AT_ADDRESS = 3;
export const isValidCreditScoreAge = (dateOfBirth) => {
    const dob = parseISO(dateOfBirth);
    const age = differenceInYears(new Date(), dob);
    return age >= MIN_AGE && age <= MAX_AGE;
};
export const needsPreviousAddress = (primaryAddress) => {
    if (!primaryAddress?.startDate)
        return false;
    const yearsAtAddress = differenceInYears(new Date(), parseISO(primaryAddress.startDate));
    return yearsAtAddress < MIN_YEARS_AT_ADDRESS;
};
export const hasValidAddress = (primaryAddress) => !!primaryAddress &&
    !!primaryAddress.postcode &&
    !!(primaryAddress.buildingNumber ||
        primaryAddress.buildingName ||
        primaryAddress.subBuildingName);
// parseISO, not new Date: next update dates are date-only ('2026-07-16'), which
// new Date reads as UTC midnight — a day early anywhere west of UTC.
export const formatDaysUntil = (dateString) => {
    const days = differenceInCalendarDays(parseISO(dateString), new Date());
    if (days <= 0) {
        return 'today';
    }
    return `in ${days} day${days === 1 ? '' : 's'}`;
};
// The sentences formatDaysUntil is read in. Kept here rather than inline in
// the components so the tests can check the full wording.
export const formatNextScoreUpdateLabel = (nextUpdateDate) => `Next update ${formatDaysUntil(nextUpdateDate)}`;
export const formatNextScoreUpdateTooltip = (nextUpdateDate) => `Your credit score is updated monthly and your next update is ${formatDaysUntil(nextUpdateDate)}. With Emma Ultimate, your score and report update in real time whenever changes are detected.`;
export const formatReportUpdateInfo = (nextUpdateDate) => {
    const next = nextUpdateDate
        ? ` Next update ${formatDaysUntil(nextUpdateDate)}`
        : '';
    return `Your report updates monthly.${next}`;
};
export const formatShortDate = (date) => {
    const parsedDate = typeof date === 'string' ? parseISO(date) : date;
    if (isThisYear(parsedDate)) {
        return format(parsedDate, 'd MMM');
    }
    return format(parsedDate, 'd MMM yy');
};
export const formatMonthLabel = (date) => {
    const parsedDate = typeof date === 'string' ? parseISO(date) : date;
    if (isThisYear(parsedDate)) {
        return format(parsedDate, 'MMM');
    }
    return format(parsedDate, "MMM ''yy");
};
export var CreditScoreBandText;
(function (CreditScoreBandText) {
    CreditScoreBandText["VERY_POOR"] = "Very poor";
    CreditScoreBandText["POOR"] = "Poor";
    CreditScoreBandText["FAIR"] = "Fair";
    CreditScoreBandText["GOOD"] = "Good";
    CreditScoreBandText["EXCELLENT"] = "Excellent";
})(CreditScoreBandText || (CreditScoreBandText = {}));
export const CREDIT_SCORE_BANDS = [
    { label: CreditScoreBandText.VERY_POOR, min: 0, max: 550 },
    { label: CreditScoreBandText.POOR, min: 551, max: 565 },
    { label: CreditScoreBandText.FAIR, min: 566, max: 603 },
    { label: CreditScoreBandText.GOOD, min: 604, max: 627 },
    { label: CreditScoreBandText.EXCELLENT, min: 628, max: 710 },
];
// Use this for testing on staging
// Production requires SDK to be configured with a real blackbox
export const STAGING_BLACKBOX = { blackBox: 'Green' };
export const isPositiveFactor = (factorColor) => factorColor === 'green';
export const getElectoralRollStatusLabel = (status) => {
    switch (status) {
        case 'Present':
            return 'On the electoral register';
        case 'NotPresent':
            return 'On the full register only';
        case 'NotConfirmed':
            return 'Registration unconfirmed';
        case 'NotFound':
            return 'Not on the electoral register';
        default:
            return null;
    }
};
export const getPaymentMonthSeverity = (status) => {
    if (!status)
        return 'unknown';
    if (status.paymentStatus === 'D' || status.accountStatus === 'DF') {
        return 'defaulted';
    }
    if (status.paymentStatus === '0')
        return 'onTime';
    const monthsLate = parseInt(status.paymentStatus ?? '', 10);
    if (!Number.isNaN(monthsLate) && monthsLate >= 1)
        return 'late';
    return 'unknown';
};
export const RECENT_SEARCH_WINDOW = 'last30Days';
export const RECENT_SEARCH_WINDOW_LABELS = {
    calendarMonth: 'this month',
    lastCalendarMonth: 'last month',
    last30Days: 'in the last 30 days',
};
const resolveReferenceDate = (referenceDate) => {
    if (referenceDate) {
        const parsed = typeof referenceDate === 'string'
            ? parseISO(referenceDate)
            : referenceDate;
        if (!Number.isNaN(parsed.getTime()))
            return parsed;
    }
    return new Date();
};
export const countRecentSearches = (searches, window = RECENT_SEARCH_WINDOW, referenceDate) => {
    if (!searches?.length)
        return 0;
    const now = resolveReferenceDate(referenceDate);
    return searches.filter((search) => {
        if (!search.searchDate)
            return false;
        const searchDate = parseISO(search.searchDate);
        if (Number.isNaN(searchDate.getTime()))
            return false;
        if (window === 'calendarMonth') {
            return isSameMonth(searchDate, now);
        }
        if (window === 'lastCalendarMonth') {
            return isSameMonth(searchDate, subMonths(now, 1));
        }
        const daysAgo = differenceInCalendarDays(now, searchDate);
        return daysAgo >= 0 && daysAgo <= 30;
    }).length;
};
const VISIBLE_TAIL_LENGTH = 4;
const MASK_PREFIX = '• • • • • •';
export const prepareAccountNumber = (accountNumber) => {
    const compact = accountNumber.replace(/[^0-9A-Za-z*]/g, '');
    const visible = compact.replace(/\*/g, '').slice(-VISIBLE_TAIL_LENGTH);
    return `${MASK_PREFIX} ${visible}`;
};
